import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { Dropdown } from 'react-bootstrap';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTags,
  faPlus,
  faSearch,
  faTimes,
  faTag,
  faClock,
  faTicketAlt,
  faCamera,
  faCheckCircle,
  faExclamationTriangle,
  faEye,
  faEdit,
  faTrash,
  faThLarge,
  faList,
  faEllipsisV,
  faArrowUp,
  faFolderOpen
} from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import TagCard from './tags/TagCard';
import TagCreateModal from './tags/TagCreateModal';
import TagViewModal from './tags/TagViewModal';
import TagCompleteModal from './tags/TagCompleteModal';
import TagDeleteModal from './tags/TagDeleteModal';
import TagPhotoModals from './tags/TagPhotoModals';
import { getStatusBadge, getIncidentTimeInfo, formatDurationMs } from './tags/tagStatusHelper';
import TicketsPagination from '../TicketsView/componentes/TicketsPagination';

const TableRowMenuToggle = React.forwardRef(({ onClick, isDark }, ref) => (
  <button
    ref={ref}
    type="button"
    className="btn btn-sm rounded-circle border-0 d-flex align-items-center justify-content-center shadow-none hover-opacity p-0"
    style={{
      width: '30px',
      height: '30px',
      color: isDark ? '#94a3b8' : '#64748b',
      backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
      cursor: 'pointer'
    }}
    onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    }}
    title="Opciones"
  >
    <FontAwesomeIcon icon={faEllipsisV} style={{ fontSize: '0.85rem' }} />
  </button>
));

/**
 * Sección principal de administración de Catálogo de Incidencias (Etiquetas)
 * Renovado con diseño moderno: bordes redondeados (16px), alineaciones centradas
 * y toolbar unificada en acciones de tabla.
 */
const TagsSettingsSection = () => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token, nombreCompleto } = useToken();
  const { showSuccessToast, showErrorToast } = useToast();

  const [tags, setTags] = useState([]);
  const [summaryTags, setSummaryTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState('todas'); // 'todas' | 'abiertas' | 'en_progreso' | 'cerradas' | 'con_tickets'
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const isInitialMount = useRef(true);

  // Lista de usuarios/agentes del sistema para asignación de responsable
  const [agentsList, setAgentsList] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);

  // Modal Crear / Editar Incidencia State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formStatus, setFormStatus] = useState(2); // 2: En Progreso por default
  const [formResponsible, setFormResponsible] = useState(null);
  const [filesList, setFilesList] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Modal Vista / Detalle de Incidencia Abierta State
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [photosList, setPhotosList] = useState([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [tagTickets, setTagTickets] = useState([]);
  const [isLoadingTagTickets, setIsLoadingTagTickets] = useState(false);

  // Modal Eliminar Incidencia State
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Modal Completar / Resolver Incidencia State
  const [itemToComplete, setItemToComplete] = useState(null);
  const [isCompleting, setIsCompleting] = useState(false);

  // Lightbox de fotos
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  // Cargar lista de agentes/usuarios del sistema
  const fetchAgents = async () => {
    if (!token) return;
    setIsLoadingAgents(true);
    try {
      const resp = await fetch('/api/SupportEngine/GetAgentsList', {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });
      if (resp.ok) {
        const result = await resp.json();
        const list = result.data || (Array.isArray(result) ? result : []);
        setAgentsList(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error('Error al cargar lista de agentes:', err);
    } finally {
      setIsLoadingAgents(false);
    }
  };

  // Cargar resumen global para las tarjetas y contadores
  const fetchSummary = async () => {
    if (!token) return;
    try {
      const response = await fetch('/api/SupportEngine/GetEtiquetas', {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });
      if (response.ok) {
        const result = await response.json();
        const list = result.data || result;
        if (Array.isArray(list)) {
          setSummaryTags(list);
        }
      }
    } catch (err) {
      console.error('Error al cargar resumen de incidencias:', err);
    }
  };

  // Cargar catálogo de incidencias desde la API filtrado por pestaña y búsqueda con paginación
  const fetchTags = async (
    searchQuery = searchTerm,
    tabToFetch = filterTab,
    pageToFetch = page,
    sizeToFetch = pageSize
  ) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery && searchQuery.trim()) params.append('searchTerm', searchQuery.trim());
      if (tabToFetch && tabToFetch !== 'todas') params.append('tab', tabToFetch);
      params.append('page', pageToFetch);
      params.append('pageSize', sizeToFetch);

      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`/api/SupportEngine/GetEtiquetas${query}`, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });

      if (response.ok) {
        const result = await response.json();
        const list = result.data || result;
        if (Array.isArray(list)) {
          setTags(list);
          const total = list.length > 0 ? (list[0].iTotalRegistros ?? list[0].totalRecords ?? list.length) : 0;
          setTotalRecords(total);
        }
      } else {
        showErrorToast('Error al cargar la lista de incidencias.');
      }
    } catch (err) {
      console.error('Error al cargar etiquetas:', err);
      showErrorToast('Error de conexión al cargar incidencias.');
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar datos al montar y con debounce al buscar
  useEffect(() => {
    if (!token) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchTags(searchTerm, filterTab, 1, pageSize);
      fetchSummary();
      fetchAgents();
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(() => {
      setPage(1);
      fetchTags(searchTerm, filterTab, 1, pageSize);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, token]);

  // Cargar fotos para una incidencia
  const loadPhotos = async tagId => {
    if (!tagId) {
      setPhotosList([]);
      return;
    }
    setIsLoadingPhotos(true);
    try {
      const response = await fetch(`/api/SupportEngine/GetIncidenciaFotos?idEtiqueta=${tagId}`, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });
      if (response.ok) {
        const result = await response.json();
        const list = result.data || result;
        setPhotosList(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error('Error al cargar fotos:', err);
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  // Cargar tickets vinculados a una incidencia
  const loadTagTickets = async tagId => {
    if (!tagId) {
      setTagTickets([]);
      return;
    }
    setIsLoadingTagTickets(true);
    try {
      const response = await fetch(`/api/SupportEngine/GetTagTickets?idEtiqueta=${tagId}`, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });
      if (response.ok) {
        const result = await response.json();
        const list = result.data || result;
        setTagTickets(Array.isArray(list) ? list : []);
      } else {
        setTagTickets([]);
      }
    } catch (err) {
      console.error('Error al cargar tickets vinculados:', err);
      setTagTickets([]);
    } finally {
      setIsLoadingTagTickets(false);
    }
  };

  // Abrir modal de creación (Imagen 1)
  const handleOpenCreate = () => {
    setSelectedTag(null);
    setFormName('');
    setFormDescription('');
    setFormStatus(1);
    setFormResponsible(null);
    setFilesList([]);
    setIsCreateModalOpen(true);
  };

  // Abrir modal de edición
  const handleOpenEdit = tag => {
    setSelectedTag(tag);
    setFormName(tag.vNombre || '');
    setFormDescription(tag.vDescripcion || '');
    setFormStatus(Number(tag.iEstadoIncidencia) || (tag.idUsuarioResponsable ? 2 : 1));
    setFormResponsible(tag.idUsuarioResponsable || null);
    setFilesList([]);
    setIsCreateModalOpen(true);
  };

  // Abrir modal de visualización (Imagen 2)
  const handleOpenView = async tag => {
    setSelectedTag(tag);
    setIsViewModalOpen(true);
    await Promise.all([loadPhotos(tag.idEtiqueta), loadTagTickets(tag.idEtiqueta)]);
  };

  // Guardar (Crear o Actualizar Incidencia)
  const handleSave = async e => {
    e.preventDefault();
    const cleanName = formName.trim();
    const cleanDesc = formDescription.trim();

    if (!cleanName) {
      showErrorToast('El título de la incidencia es obligatorio.');
      return;
    }
    if (!cleanDesc) {
      showErrorToast('La descripción del problema es obligatoria.');
      return;
    }

    setIsSaving(true);
    try {
      const determinedStatus = selectedTag && Number(selectedTag.iEstadoIncidencia) === 3
        ? 3
        : (formResponsible ? 2 : 1);

      const payload = {
        idEtiqueta: selectedTag ? selectedTag.idEtiqueta : null,
        vNombre: cleanName,
        vDescripcion: cleanDesc,
        iEstadoIncidencia: determinedStatus,
        idUsuarioResponsable: formResponsible ? Number(formResponsible) : null,
        vCreatedUser: nombreCompleto || 'Sistema'
      };

      const response = await fetch('/api/SupportEngine/SaveEtiqueta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        let msg = result.message || 'Error al guardar la incidencia';
        if (result.details) {
          try {
            const parsed = typeof result.details === 'string' ? JSON.parse(result.details) : result.details;
            if (parsed.message) msg = parsed.message;
          } catch (_) {
            msg = result.details;
          }
        }
        showErrorToast(msg);
        return;
      }

      // Si hay archivos para subir
      let tagIdToUse = selectedTag?.idEtiqueta;
      if (!tagIdToUse && filesList.length > 0) {
        try {
          const tagsResp = await fetch(`/api/SupportEngine/GetEtiquetas?searchTerm=${encodeURIComponent(cleanName)}`, {
            headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
          });
          if (tagsResp.ok) {
            const tagsResult = await tagsResp.json().catch(() => ({}));
            const list = tagsResult.data || tagsResult;
            if (Array.isArray(list)) {
              const foundTag = list.find(t => t.vNombre.trim().toLowerCase() === cleanName.toLowerCase());
              if (foundTag) tagIdToUse = foundTag.idEtiqueta;
            }
          }
        } catch (errSearch) {
          console.error('Error al resolver ID de la nueva incidencia:', errSearch);
        }
      }

      if (tagIdToUse && filesList.length > 0) {
        for (const f of filesList.filter(f => f.isPendingUpload)) {
          try {
            await fetch('/api/SupportEngine/SaveIncidenciaFoto', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: 'Bearer ' + token
              },
              body: JSON.stringify({
                idEtiqueta: tagIdToUse,
                vNombreArchivo: f.fileName,
                vUrlFoto: f.dataUrl,
                vDescripcion: 'Evidencia adjunta',
                vCreatedUser: nombreCompleto || 'Sistema'
              })
            });
          } catch (errPhoto) {
            console.error('Error al subir evidencia:', errPhoto);
          }
        }
      }

      showSuccessToast(selectedTag ? 'Incidencia actualizada exitosamente.' : 'Incidencia creada exitosamente.');
      setIsCreateModalOpen(false);
      fetchTags(searchTerm, filterTab);
      fetchSummary();
    } catch (err) {
      console.error('Error al guardar incidencia:', err);
      showErrorToast('Error de conexión al guardar la incidencia.');
    } finally {
      setIsSaving(false);
    }
  };

  // Abrir confirmación de eliminación
  const handleOpenDelete = tag => {
    setItemToDelete(tag);
  };

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);

    try {
      const payload = {
        idEtiqueta: itemToDelete.idEtiqueta,
        vUpdatedUser: nombreCompleto || 'Sistema'
      };

      const response = await fetch('/api/SupportEngine/DeleteEtiqueta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        showErrorToast('No se pudo desactivar la incidencia.');
        return;
      }

      showSuccessToast('Incidencia eliminada exitosamente.');
      setItemToDelete(null);
      fetchTags(searchTerm, filterTab);
      fetchSummary();
    } catch (err) {
      console.error('Error al eliminar etiqueta:', err);
      showErrorToast('Error de conexión al eliminar la incidencia.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Abrir modal de completar/resolver incidencia
  const handleOpenComplete = tag => {
    if (!tag) return;
    setItemToComplete(tag);
  };

  // Confirmar resolver incidencia
  const handleConfirmComplete = async customMessage => {
    if (!itemToComplete) return;
    setIsCompleting(true);

    try {
      const payload = {
        idEtiqueta: itemToComplete.idEtiqueta,
        vUpdatedUser: nombreCompleto || 'Sistema',
        vMensajeResolucion: customMessage || null
      };

      const response = await fetch('/api/SupportEngine/CompleteEtiqueta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok && (result.success || result.iResponseType === 1)) {
        showSuccessToast('Incidencia resuelta exitosamente.');
        setItemToComplete(null);
        if (selectedTag && selectedTag.idEtiqueta === itemToComplete.idEtiqueta) {
          setSelectedTag(prev => (prev ? { ...prev, iEstadoIncidencia: 3 } : null));
          loadTagTickets(itemToComplete.idEtiqueta);
        }
        fetchTags(searchTerm, filterTab);
        fetchSummary();
      } else {
        showErrorToast(result.message || result.vErrorDescription || 'No se pudo resolver la incidencia.');
      }
    } catch (err) {
      console.error('Error al resolver incidencia:', err);
      showErrorToast('Error de conexión al resolver la incidencia.');
    } finally {
      setIsCompleting(false);
    }
  };

  // Fuente de datos para contadores globales y métricas
  const summarySource = summaryTags.length > 0 ? summaryTags : tags;

  // Métricas calculadas (de la fuente global)
  const metrics = useMemo(() => {
    const activeIncidents = summarySource.filter(t => Number(t.iEstadoIncidencia) !== 3);
    const resolvedIncidents = summarySource.filter(t => Number(t.iEstadoIncidencia) === 3);
    const affectedTickets = activeIncidents.reduce((acc, t) => acc + (t.iTotalTickets || 0), 0);

    // Tiempo promedio de resolución (exclusivamente de las incidencias resueltas, calculado desde progreso o creación)
    let totalResolvedMs = 0;
    let resolvedWithDurationCount = 0;

    resolvedIncidents.forEach(t => {
      // 1. Si la BD ya trae iTiempoResolucionMinutos
      if (t.iTiempoResolucionMinutos !== null && t.iTiempoResolucionMinutos !== undefined) {
        const mins = Number(t.iTiempoResolucionMinutos);
        if (!isNaN(mins) && mins >= 0) {
          totalResolvedMs += mins * 60 * 1000;
          resolvedWithDurationCount += 1;
          return;
        }
      }

      // 2. Si tiene fechas registradas (resolución - progreso o creación)
      const startDate = t.dFechaProgreso || t.dCreatedDate || t.createdDate || t.dFechaCreacion;
      const endDate = t.dFechaResolucion || t.dUpdatedDate || t.updatedDate || t.dFechaCierre || t.dFechaActualizacion;
      if (startDate && endDate) {
        const start = new Date(startDate).getTime();
        const end = new Date(endDate).getTime();
        if (!isNaN(start) && !isNaN(end) && end >= start) {
          totalResolvedMs += (end - start);
          resolvedWithDurationCount += 1;
          return;
        }
      }

      // 3. Fallback para registros resueltos históricos sin fechas de cierre: contabilizar duración mínima (< 1m)
      totalResolvedMs += 0;
      resolvedWithDurationCount += 1;
    });

    const avgResolutionMs = resolvedWithDurationCount > 0
      ? Math.round(totalResolvedMs / resolvedWithDurationCount)
      : null;
    const avgResolutionTime = resolvedIncidents.length > 0
      ? (formatDurationMs(avgResolutionMs) || '< 1m')
      : null;

    return {
      activeCount: activeIncidents.length,
      affectedTicketsCount: affectedTickets,
      resolvedCount: resolvedIncidents.length,
      avgResolutionTime: avgResolutionTime
    };
  }, [summarySource]);

  // Lista a renderizar: filtrada directamente por la BD según la pestaña seleccionada
  const filteredTags = tags;

  // Contadores para las pestañas y tarjetas
  const tabCounts = useMemo(() => {
    const inProgressList = summarySource.filter(t => Number(t.iEstadoIncidencia) === 2);
    const inProgressWithTickets = inProgressList.filter(t => (t.iTotalTickets || 0) > 0);
    const inProgressTicketsCount = inProgressWithTickets.reduce((sum, t) => sum + (t.iTotalTickets || 0), 0);

    return {
      todas: summarySource.length,
      activas: summarySource.filter(t => Number(t.iEstadoIncidencia) !== 3).length,
      abiertas: summarySource.filter(t => Number(t.iEstadoIncidencia) === 1).length,
      en_progreso: inProgressList.length,
      en_progreso_con_tickets: inProgressWithTickets.length,
      en_progreso_tickets_count: inProgressTicketsCount,
      cerradas: summarySource.filter(t => Number(t.iEstadoIncidencia) === 3).length,
      con_tickets: inProgressWithTickets.length
    };
  }, [summarySource]);

  // Banderas de selección activa para las tarjetas de métricas
  const isAbiertasActive = filterTab === 'abiertas';
  const isEnProgresoActive = filterTab === 'en_progreso';
  const isCerradasActive = filterTab === 'cerradas';

  // Opciones de pestañas de filtrado sincronizadas con las tarjetas
  const filterTabs = [
    { key: 'todas', label: 'Todas', count: tabCounts.todas, activeBg: '#4f46e5', activeBorder: '#4f46e5' },
    { key: 'abiertas', label: 'Abiertas', count: tabCounts.abiertas, dotColor: '#f59e0b', activeBg: '#d97706', activeBorder: '#d97706' },
    { key: 'en_progreso', label: 'En Progreso', count: tabCounts.en_progreso, dotColor: '#3b82f6', activeBg: '#2563eb', activeBorder: '#2563eb' },
    { key: 'cerradas', label: 'Resueltas', count: tabCounts.cerradas, dotColor: '#10b981', activeBg: '#059669', activeBorder: '#059669' }
  ];

  // Manejador al hacer clic en una pestaña para refrescar datos desde la base de datos
  const handleTabClick = key => {
    setFilterTab(key);
    setPage(1);
    fetchTags(searchTerm, key, 1, pageSize);
  };

  // Manejadores de paginación
  const handlePageChange = newPage => {
    setPage(newPage);
    fetchTags(searchTerm, filterTab, newPage, pageSize);
  };

  const handlePageSizeChange = newSize => {
    setPageSize(newSize);
    setPage(1);
    fetchTags(searchTerm, filterTab, 1, newSize);
  };

  // Manejador al hacer clic en una tarjeta de métrica para alternar filtro y refrescar datos
  const handleCardClick = tabKey => {
    const nextTab = filterTab === tabKey ? 'todas' : tabKey;
    handleTabClick(nextTab);
  };



  return (
    <div className="d-flex flex-column mb-4" style={{ gap: '24px' }}>
      {/* HEADER LIVE FEED Y BOTÓN NUEVA INCIDENCIA (IMAGEN 3) */}
      <div
        className="p-4 border d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{
          background: isDark
            ? 'linear-gradient(135deg, #152336 0%, #0f1927 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
          borderRadius: '20px',
          boxShadow: isDark ? '0 8px 24px -4px rgba(0,0,0,0.3)' : '0 8px 24px -4px rgba(0,0,0,0.04)'
        }}
      >
        <div className="d-flex flex-column gap-1">
          <div className="d-flex align-items-center gap-2">
            <h4
              className="fw-bold mb-0"
              style={{
                color: isDark ? '#f8fafc' : '#0f172a',
                fontSize: '1.45rem',
                letterSpacing: '-0.3px'
              }}
            >
              Incidencias
            </h4>

          </div>
          <p className="small mb-0 text-muted" style={{ fontSize: '0.86rem' }}>
            Gestión, seguimiento y resolución de incidencias técnicas en tiempo real.
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm rounded-pill px-4 py-2 fw-semibold d-flex align-items-center gap-2 shadow-none text-white"
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              border: 'none',
              boxShadow: '0 4px 14px rgba(79, 70, 229, 0.35)',
              fontSize: '0.86rem'
            }}
            onClick={handleOpenCreate}
          >
            <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.78rem' }} />
            <span>Nueva Incidencia</span>
          </button>
        </div>
      </div>

      {/* METRIC CARDS REDONDEADAS Y ESTILIZADAS (20px) - 3 TARJETAS PRINCIPALES */}
      <div className="row g-3" style={{ marginTop: 0, marginBottom: 0 }}>
        {/* Card 1: INCIDENCIAS ABIERTAS / PENDIENTES */}
        <div className="col-12 col-md-4">
          <div
            role="button"
            tabIndex={0}
            onClick={() => handleCardClick('abiertas')}
            className="p-4 border h-100 d-flex flex-column justify-content-between transition-all"
            style={{
              cursor: 'pointer',
              userSelect: 'none',
              backgroundColor: isDark
                ? (isAbiertasActive ? '#192638' : '#141e2c')
                : (isAbiertasActive ? '#fefce8' : '#ffffff'),
              borderColor: isAbiertasActive
                ? (isDark ? 'rgba(245, 158, 11, 0.55)' : '#f59e0b')
                : (isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0'),
              borderWidth: '1px',
              borderRadius: '18px',
              boxShadow: isAbiertasActive
                ? (isDark ? '0 8px 24px -4px rgba(0,0,0,0.45)' : '0 8px 20px -4px rgba(0,0,0,0.08)')
                : (isDark ? '0 4px 16px -2px rgba(0,0,0,0.25)' : '0 4px 14px -2px rgba(0,0,0,0.03)')
            }}
            title="Ver incidencias abiertas / pendientes de asignar"
          >
            {/* Header: Título a la izquierda, Badge a la derecha */}
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span
                className="fw-bold text-uppercase d-flex align-items-center gap-2"
                style={{
                  fontSize: '0.72rem',
                  letterSpacing: '0.6px',
                  color: isAbiertasActive ? (isDark ? '#fde68a' : '#b45309') : (isDark ? '#94a3b8' : '#64748b')
                }}
              >
                <span>Abiertas</span>
              </span>
              <span
                className="badge rounded-pill fw-bold d-inline-flex align-items-center"
                style={{
                  backgroundColor: isDark ? 'rgba(245, 158, 11, 0.20)' : '#fef3c7',
                  color: isDark ? '#fcd34d' : '#b45309',
                  border: `1px solid ${isDark ? 'rgba(245, 158, 11, 0.40)' : '#fde68a'}`,
                  fontSize: '0.74rem',
                  padding: '4px 11px',
                  boxShadow: isDark ? '0 0 10px rgba(245, 158, 11, 0.15)' : 'none'
                }}
              >
                <span>Creadas</span>
              </span>
            </div>

            {/* Body: Número + Subtítulo a la izquierda, Ícono a la derecha */}
            <div className="d-flex align-items-center justify-content-between pt-1">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="fw-bold"
                    style={{
                      fontSize: '2.2rem',
                      color: isDark ? '#f8fafc' : '#0f172a',
                      lineHeight: '1',
                      letterSpacing: '-0.5px'
                    }}
                  >
                    {tabCounts.abiertas}
                  </div>
                </div>
                <div
                  className="mt-2"
                  style={{
                    fontSize: '0.82rem',
                    lineHeight: '1.4',
                    color: isDark ? '#cbd5e1' : '#475569'
                  }}
                >
                  Nuevas creadas sin asignar
                </div>
              </div>

              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0 ms-3"
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '14px',
                  backgroundColor: isDark ? 'rgba(245, 158, 11, 0.14)' : '#fef3c7',
                  border: `1px solid ${isDark ? 'rgba(245, 158, 11, 0.25)' : '#fde68a'}`,
                  color: isDark ? '#fbbf24' : '#d97706',
                  boxShadow: 'none'
                }}
              >
                <FontAwesomeIcon icon={faFolderOpen} style={{ fontSize: '1.15rem' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: EN PROGRESO (CON DESGLOSE DE TICKETS INTEGRADO) */}
        <div className="col-12 col-md-4">
          <div
            role="button"
            tabIndex={0}
            onClick={() => handleCardClick('en_progreso')}
            className="p-4 border h-100 d-flex flex-column justify-content-between transition-all"
            style={{
              cursor: 'pointer',
              userSelect: 'none',
              backgroundColor: isDark
                ? (isEnProgresoActive ? '#192638' : '#141e2c')
                : (isEnProgresoActive ? '#eff6ff' : '#ffffff'),
              borderColor: isEnProgresoActive
                ? (isDark ? 'rgba(59, 130, 246, 0.55)' : '#3b82f6')
                : (isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0'),
              borderWidth: '1px',
              borderRadius: '18px',
              boxShadow: isEnProgresoActive
                ? (isDark ? '0 8px 24px -4px rgba(0,0,0,0.45)' : '0 8px 20px -4px rgba(0,0,0,0.08)')
                : (isDark ? '0 4px 16px -2px rgba(0,0,0,0.25)' : '0 4px 14px -2px rgba(0,0,0,0.03)')
            }}
            title="Ver incidencias en atención activa"
          >
            {/* Header: Título a la izquierda, Badge a la derecha */}
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span
                className="fw-bold text-uppercase d-flex align-items-center gap-2"
                style={{
                  fontSize: '0.72rem',
                  letterSpacing: '0.6px',
                  color: isEnProgresoActive ? (isDark ? '#93c5fd' : '#1d4ed8') : (isDark ? '#94a3b8' : '#64748b')
                }}
              >
                <span>En Progreso</span>
              </span>
              <span
                className="badge rounded-pill fw-bold d-inline-flex align-items-center"
                style={{
                  backgroundColor: isDark ? 'rgba(59, 130, 246, 0.22)' : '#dbeafe',
                  color: isDark ? '#93c5fd' : '#1e40af',
                  border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.45)' : '#93c5fd'}`,
                  fontSize: '0.74rem',
                  padding: '4px 11px',
                  boxShadow: isDark ? '0 0 10px rgba(59, 130, 246, 0.18)' : 'none'
                }}
              >
                <span>En Atención</span>
              </span>
            </div>

            {/* Body: Número + Indicador de incremento de tickets/atención + Subtítulo */}
            <div className="d-flex align-items-center justify-content-between pt-1">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="fw-bold"
                    style={{
                      fontSize: '2.2rem',
                      color: isDark ? '#f8fafc' : '#0f172a',
                      lineHeight: '1',
                      letterSpacing: '-0.5px'
                    }}
                  >
                    {tabCounts.en_progreso}
                  </div>
                  {tabCounts.en_progreso_tickets_count > 0 && (
                    <span
                      className="badge rounded-pill fw-bold d-inline-flex align-items-center"
                      style={{
                        fontSize: '0.72rem',
                        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.25)' : '#dbeafe',
                        color: isDark ? '#60a5fa' : '#1d4ed8',
                        border: `1px solid ${isDark ? 'rgba(96, 165, 250, 0.5)' : '#93c5fd'}`,
                        padding: '3px 8px',
                        gap: '4px',
                        boxShadow: isDark ? '0 0 10px rgba(59, 130, 246, 0.2)' : 'none'
                      }}
                      title="Tickets asociados a incidencias en progreso"
                    >
                      <FontAwesomeIcon icon={faArrowUp} style={{ fontSize: '0.62rem' }} />
                      <span>+{tabCounts.en_progreso_tickets_count} tickets</span>
                    </span>
                  )}
                </div>
                <div
                  className="mt-2"
                  style={{
                    fontSize: '0.82rem',
                    lineHeight: '1.4',
                    color: isDark ? '#cbd5e1' : '#475569'
                  }}
                >
                  {tabCounts.en_progreso_con_tickets > 0 ? (
                    <>
                      <strong
                        className="fw-bold"
                        style={{ color: isDark ? '#93c5fd' : '#1d4ed8' }}
                      >
                        {tabCounts.en_progreso_con_tickets}
                      </strong>{' '}
                      <span>{tabCounts.en_progreso_con_tickets === 1 ? 'incidencia con tickets' : 'incidencias con tickets'}</span>{' '}
                      <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                        ({tabCounts.en_progreso_tickets_count} {tabCounts.en_progreso_tickets_count === 1 ? 'ticket' : 'tickets'})
                      </span>
                    </>
                  ) : (
                    <span>0 incidencias con tickets</span>
                  )}
                </div>
              </div>

              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0 ms-3"
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '14px',
                  backgroundColor: isDark ? 'rgba(59, 130, 246, 0.16)' : '#dbeafe',
                  border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.35)' : '#bfdbfe'}`,
                  color: isDark ? '#60a5fa' : '#2563eb',
                  boxShadow: 'none'
                }}
              >
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '1.15rem' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: TOTAL RESUELTAS */}
        <div className="col-12 col-md-4">
          <div
            role="button"
            tabIndex={0}
            onClick={() => handleCardClick('cerradas')}
            className="p-4 border h-100 d-flex flex-column justify-content-between transition-all"
            style={{
              cursor: 'pointer',
              userSelect: 'none',
              backgroundColor: isDark
                ? (isCerradasActive ? '#192638' : '#141e2c')
                : (isCerradasActive ? '#f0fdf4' : '#ffffff'),
              borderColor: isCerradasActive
                ? (isDark ? 'rgba(16, 185, 129, 0.55)' : '#10b981')
                : (isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0'),
              borderWidth: '1px',
              borderRadius: '18px',
              boxShadow: isCerradasActive
                ? (isDark ? '0 8px 24px -4px rgba(0,0,0,0.45)' : '0 8px 20px -4px rgba(0,0,0,0.08)')
                : (isDark ? '0 4px 16px -2px rgba(0,0,0,0.25)' : '0 4px 14px -2px rgba(0,0,0,0.03)')
            }}
            title="Ver incidencias resueltas / cerradas"
          >
            {/* Header: Título a la izquierda, Badge a la derecha */}
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span
                className="fw-bold text-uppercase d-flex align-items-center gap-2"
                style={{
                  fontSize: '0.72rem',
                  letterSpacing: '0.6px',
                  color: isCerradasActive ? (isDark ? '#6ee7b7' : '#047857') : (isDark ? '#94a3b8' : '#64748b')
                }}
              >
                <span>Total Resueltas</span>
              </span>
              <span
                className="badge rounded-pill fw-bold d-inline-flex align-items-center"
                style={{
                  backgroundColor: isDark ? 'rgba(16, 185, 129, 0.22)' : '#d1fae5',
                  color: isDark ? '#6ee7b7' : '#047857',
                  border: `1px solid ${isDark ? 'rgba(16, 185, 129, 0.45)' : '#a7f3d0'}`,
                  fontSize: '0.74rem',
                  padding: '4px 11px',
                  boxShadow: isDark ? '0 0 10px rgba(16, 185, 129, 0.18)' : 'none'
                }}
              >
                <span>{metrics.avgResolutionTime ? `Prom. ${metrics.avgResolutionTime}` : '0m'}</span>
              </span>
            </div>

            {/* Body: Número + Subtítulo a la izquierda, Ícono a la derecha */}
            <div className="d-flex align-items-center justify-content-between pt-1">
              <div>
                <div
                  className="fw-bold"
                  style={{
                    fontSize: '2.2rem',
                    color: isDark ? '#f8fafc' : '#0f172a',
                    lineHeight: '1',
                    letterSpacing: '-0.5px'
                  }}
                >
                  {metrics.resolvedCount}
                </div>
                <div
                  className="d-flex align-items-center flex-wrap"
                  style={{
                    marginTop: '10px',
                    fontSize: '0.82rem',
                    lineHeight: '1.4',
                    color: isDark ? '#cbd5e1' : '#475569',
                    gap: '6px'
                  }}
                >
                  <span>Tiempo prom. resolución:</span>
                  <strong
                    className="fw-bold ms-1"
                    style={{
                      color: isDark ? '#34d399' : '#059669',
                      fontSize: '0.84rem'
                    }}
                  >
                    {metrics.avgResolutionTime || '--'}
                  </strong>
                </div>
              </div>

              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0 ms-3"
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '14px',
                  backgroundColor: isDark ? 'rgba(16, 185, 129, 0.16)' : '#d1fae5',
                  border: `1px solid ${isDark ? 'rgba(16, 185, 129, 0.35)' : '#a7f3d0'}`,
                  color: isDark ? '#34d399' : '#059669',
                  boxShadow: 'none'
                }}
              >
                <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '1.15rem' }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* BARRA DE FILTROS Y BÚSQUEDA */}
      <div
        className="p-3 px-3.5 border d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{
          background: isDark
            ? 'linear-gradient(135deg, #152233 0%, #101a28 100%)'
            : '#ffffff',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
          borderRadius: '20px',
          boxShadow: isDark ? '0 4px 18px rgba(0,0,0,0.2)' : '0 4px 14px rgba(0,0,0,0.03)'
        }}
      >
        {/* PESTAÑAS DE FILTRADO HORIZONTALES */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {filterTabs.map(tab => {
            const isActive = filterTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                className={`btn btn-sm rounded-pill px-3 py-1.5 fw-semibold shadow-none border d-inline-flex align-items-center justify-content-center gap-2 ${isActive ? 'active' : ''
                  }`}
                style={{
                  height: '36px',
                  backgroundColor: isActive
                    ? tab.activeBg
                    : isDark
                      ? '#1a2636'
                      : '#f8fafd',
                  borderColor: isActive
                    ? tab.activeBorder
                    : isDark
                      ? '#2e4157'
                      : '#cbd5e1',
                  color: isActive
                    ? '#ffffff'
                    : isDark
                      ? '#cbd5e1'
                      : '#475569',
                  fontSize: '0.80rem'
                }}
                onClick={() => handleTabClick(tab.key)}
              >
                {tab.dotColor && (
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: isActive ? '#ffffff' : tab.dotColor,
                      marginRight: '6px',
                      flexShrink: 0
                    }}
                  />
                )}
                <span>{tab.label}</span>
                <span
                  className="badge rounded-pill px-2 py-0.5"
                  style={{
                    backgroundColor: isActive
                      ? 'rgba(255,255,255,0.25)'
                      : isDark
                        ? '#25374c'
                        : '#e2e8f0',
                    color: isActive ? '#ffffff' : 'inherit',
                    fontSize: '0.70rem'
                  }}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Buscador y Toggle de Vista */}
        <div className="d-flex align-items-center gap-2 ms-auto flex-grow-1" style={{ maxWidth: '440px' }}>
          <div
            className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill border w-100"
            style={{
              height: '38px',
              backgroundColor: isDark ? '#1a2636' : '#f8fafd',
              borderColor: isDark ? '#2e4157' : '#cbd5e1'
            }}
          >
            <FontAwesomeIcon icon={faSearch} className="text-muted" style={{ fontSize: '0.82rem' }} />
            <input
              type="text"
              className="border-0 bg-transparent shadow-none w-100"
              style={{
                fontSize: '0.84rem',
                outline: 'none',
                color: isDark ? '#f8fafc' : '#0f172a'
              }}
              placeholder="Buscar por título, ID (#INC) o responsable..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="btn btn-sm p-0 text-muted border-0 bg-transparent"
                onClick={() => setSearchTerm('')}
              >
                <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.75rem' }} />
              </button>
            )}
          </div>

          {/* Toggle de vista: Tabla / Cards */}
          <div
            className="d-flex align-items-center p-0.5 rounded-pill border flex-shrink-0"
            style={{
              height: '38px',
              backgroundColor: isDark ? '#1a2636' : '#f8fafd',
              borderColor: isDark ? '#2e4157' : '#cbd5e1'
            }}
          >
            <button
              type="button"
              className={`btn btn-sm rounded-circle p-0 d-flex align-items-center justify-content-center border-0 shadow-none ${viewMode === 'table' ? 'bg-primary text-white' : 'text-muted'
                }`}
              style={{ width: '32px', height: '32px', fontSize: '0.80rem' }}
              onClick={() => setViewMode('table')}
              title="Vista de lista / tabla"
            >
              <FontAwesomeIcon icon={faList} />
            </button>
            <button
              type="button"
              className={`btn btn-sm rounded-circle p-0 d-flex align-items-center justify-content-center border-0 shadow-none ${viewMode === 'cards' ? 'bg-primary text-white' : 'text-muted'
                }`}
              style={{ width: '32px', height: '32px', fontSize: '0.80rem' }}
              onClick={() => setViewMode('cards')}
              title="Vista en tarjetas"
            >
              <FontAwesomeIcon icon={faThLarge} />
            </button>
          </div>
        </div>
      </div>

      {/* CONTENIDO PRINCIPAL: TABLA (IMAGEN 3) O CARDS */}
      <div
        className="card border shadow-none overflow-hidden"
        style={{
          backgroundColor: isDark ? '#141e2c' : '#ffffff',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
          borderRadius: '20px',
          boxShadow: isDark ? '0 10px 25px -4px rgba(0,0,0,0.3)' : '0 10px 25px -4px rgba(0,0,0,0.04)'
        }}
      >
        <div className="card-body p-0">
          {isLoading ? (
            <div className="py-5 text-center text-muted">
              <div className="spinner-border spinner-border-sm text-primary mb-2" role="status" />
              <div className="small">Cargando incidencias...</div>
            </div>
          ) : filteredTags.length === 0 ? (
            <div
              className="text-center py-5 m-3 rounded-4 border border-dashed"
              style={{ borderColor: isDark ? '#2e4157' : '#cbd5e1', borderRadius: '16px' }}
            >
              <FontAwesomeIcon icon={faTag} className="text-muted mb-2" style={{ fontSize: '2rem', opacity: 0.4 }} />
              <h6 className="fw-semibold mb-1" style={{ color: isDark ? '#f8fafc' : '#0f172a' }}>
                No se encontraron incidencias
              </h6>
              <p className="small text-muted mb-3" style={{ maxWidth: '400px', margin: '0 auto', fontSize: '0.82rem' }}>
                {searchTerm
                  ? `No hay incidencias que coincidan con "${searchTerm}".`
                  : filterTab === 'activas'
                    ? 'No hay incidencias activas actualmente.'
                    : filterTab === 'abiertas'
                      ? 'No hay incidencias abiertas actualmente.'
                      : filterTab === 'en_progreso'
                        ? 'No hay incidencias en progreso actualmente.'
                        : filterTab === 'cerradas'
                          ? 'No hay incidencias cerradas o resueltas.'
                          : filterTab === 'con_tickets'
                            ? 'No hay incidencias en curso con tickets asociados actualmente.'
                            : 'Aún no has registrado ninguna incidencia. Crea una para comenzar.'}
              </p>
              {!searchTerm && (
                <button
                  type="button"
                  className="btn btn-sm rounded-pill px-3.5 py-1.5 fw-semibold shadow-none text-white"
                  style={{ backgroundColor: '#4f46e5' }}
                  onClick={handleOpenCreate}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" /> Crear incidencia
                </button>
              )}
            </div>
          ) : viewMode === 'table' ? (
            /* VISTA DE TABLA CON ALINEACIONES Y CENTRADO PULIDO */
            <div className="table-responsive" style={{ minHeight: '220px' }}>
              <table
                className="table table-hover align-middle mb-0"
                style={{
                  color: isDark ? '#f8fafc' : '#0f172a',
                  fontSize: '0.84rem',
                  tableLayout: 'fixed',
                  minWidth: '930px'
                }}
              >
                <thead
                  style={{
                    backgroundColor: isDark ? '#172537' : '#f8fafc',
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.06)' : '#e2e8f0'
                  }}
                >
                  <tr
                    className="text-uppercase small fw-bold text-muted"
                    style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}
                  >
                    <th className="ps-4 py-3.5" style={{ width: '15%', minWidth: '155px' }}>
                      ID & Estatus
                    </th>
                    <th className="py-3.5" style={{ width: '32%', minWidth: '250px' }}>
                      Título & Tiempo
                    </th>
                    <th className="py-3.5" style={{ width: '25%', minWidth: '220px' }}>
                      Responsable
                    </th>
                    <th className="py-3.5 text-center" style={{ width: '11%', minWidth: '115px' }}>
                      Tickets
                    </th>
                    <th className="py-3.5 text-center" style={{ width: '11%', minWidth: '120px' }}>
                      Evidencias
                    </th>
                    <th className="pe-4 py-3.5 text-center" style={{ width: '6%', minWidth: '70px' }}>
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody style={{ borderColor: isDark ? '#1e2c3d' : '#f1f5f9' }}>
                  {filteredTags.map((tag, index) => {
                    const status = getStatusBadge(tag.iEstadoIncidencia, isDark);
                    const timeInfo = getIncidentTimeInfo(tag);
                    const isResolved = Number(tag.iEstadoIncidencia) === 3;
                    const isNearBottom = index >= Math.max(1, filteredTags.length - 2);

                    return (
                      <tr
                        key={tag.idEtiqueta}
                        className="cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleOpenView(tag)}
                      >
                        {/* ID & ESTATUS */}
                        <td className="ps-4 py-3">
                          <div className="d-flex flex-column align-items-start" style={{ gap: '6px' }}>
                            <span
                              className="fw-bold"
                              style={{
                                color: isDark ? '#818cf8' : '#4338ca',
                                fontSize: '0.86rem',
                                letterSpacing: '0.2px'
                              }}
                            >
                              #INC-{tag.idEtiqueta}
                            </span>
                            <span
                              className="badge px-2.5 py-1 rounded-pill fw-semibold d-inline-flex align-items-center justify-content-center"
                              style={{
                                backgroundColor: status.bg,
                                color: status.color,
                                border: `1px solid ${status.border}`,
                                fontSize: '0.70rem',
                                minWidth: '92px'
                              }}
                            >
                              <span
                                style={{
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  backgroundColor: status.color,
                                  marginRight: '6px',
                                  flexShrink: 0
                                }}
                              />
                              <span>{status.label}</span>
                            </span>
                          </div>
                        </td>

                        {/* TÍTULO & TIEMPO */}
                        <td className="py-3 pe-3">
                          <div
                            className="fw-bold mb-1 text-truncate"
                            style={{ maxWidth: '100%', fontSize: '0.88rem' }}
                            title={tag.vNombre}
                          >
                            {tag.vNombre}
                          </div>
                          {isResolved ? (
                            <span
                              className="badge px-2.5 py-1 rounded-pill fw-semibold d-inline-flex align-items-center"
                              style={{
                                backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#d1fae5',
                                color: isDark ? '#34d399' : '#047857',
                                border: `1px solid ${isDark ? 'rgba(16, 185, 129, 0.3)' : '#a7f3d0'}`,
                                fontSize: '0.72rem',
                                gap: '6px'
                              }}
                              title={`Tiempo de resolución: ${timeInfo.duration || timeInfo.label}`}
                            >
                              <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '0.70rem' }} />
                              <span>{timeInfo.duration ? `Resuelta en ${timeInfo.duration}` : timeInfo.label}</span>
                            </span>
                          ) : (
                            <div
                              className="small d-flex align-items-center gap-2"
                              style={{
                                gap: '6px',
                                fontSize: '0.74rem',
                                color: isDark ? '#94a3b8' : '#64748b'
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faClock}
                                style={{
                                  fontSize: '0.68rem',
                                  color: '#6366f1'
                                }}
                              />
                              <span>{timeInfo.label}</span>
                            </div>
                          )}
                        </td>

                        {/* RESPONSABLE Y CREADOR */}
                        <td className="py-3 pe-3">
                          <div
                            className="fw-semibold text-truncate"
                            style={{
                              fontSize: '0.84rem',
                              color: tag.vResponsable
                                ? (isDark ? '#f8fafc' : '#1e293b')
                                : (isDark ? '#64748b' : '#94a3b8'),
                              maxWidth: '100%'
                            }}
                            title={tag.vResponsable || 'Sin asignar'}
                          >
                            {tag.vResponsable || <span className="fst-italic opacity-75">Sin asignar</span>}
                          </div>
                          {tag.vCreatedUser && (
                            <div
                              className="text-truncate mt-1 text-muted"
                              style={{
                                fontSize: '0.72rem',
                                color: isDark ? '#94a3b8' : '#64748b',
                                maxWidth: '100%'
                              }}
                              title={`Creado por ${tag.vCreatedUser}`}
                            >
                              <span>Creada por </span>
                              <span className="fw-medium" style={{ color: isDark ? '#cbd5e1' : '#475569' }}>
                                {tag.vCreatedUser}
                              </span>
                            </div>
                          )}
                        </td>

                        {/* TICKETS VINCULADOS (CENTRADO) */}
                        <td className="py-3 text-center">
                          <div className="d-flex justify-content-center align-items-center">
                            <span
                              className="badge px-3 py-1.5 rounded-pill fw-semibold d-inline-flex align-items-center justify-content-center gap-2"
                              style={{
                                gap: '6px',
                                backgroundColor:
                                  (tag.iTotalTickets || 0) > 0
                                    ? isDark
                                      ? 'rgba(99, 102, 241, 0.2)'
                                      : '#e0e7ff'
                                    : isDark
                                      ? '#1a2636'
                                      : '#f1f5f9',
                                color:
                                  (tag.iTotalTickets || 0) > 0
                                    ? isDark
                                      ? '#a5b4fc'
                                      : '#4338ca'
                                    : isDark
                                      ? '#94a3b8'
                                      : '#64748b',
                                fontSize: '0.75rem',
                                minWidth: '100px',
                                border: `1px solid ${(tag.iTotalTickets || 0) > 0
                                  ? isDark
                                    ? 'rgba(99, 102, 241, 0.35)'
                                    : '#c7d2fe'
                                  : isDark
                                    ? 'rgba(255,255,255,0.05)'
                                    : '#e2e8f0'
                                  }`
                              }}
                            >
                              <FontAwesomeIcon icon={faTicketAlt} style={{ fontSize: '0.70rem' }} />
                              <span>{tag.iTotalTickets || 0} tickets</span>
                            </span>
                          </div>
                        </td>

                        {/* EVIDENCIAS (CENTRADO) */}
                        <td className="py-3 text-center">
                          <div className="d-flex justify-content-center align-items-center">
                            <span
                              className="badge px-3 py-1.5 rounded-pill fw-semibold d-inline-flex align-items-center justify-content-center gap-2"
                              style={{
                                gap: '6px',
                                backgroundColor:
                                  (tag.iTotalFotos || 0) > 0
                                    ? isDark
                                      ? 'rgba(14, 165, 233, 0.2)'
                                      : '#e0f2fe'
                                    : isDark
                                      ? '#1a2636'
                                      : '#f1f5f9',
                                color:
                                  (tag.iTotalFotos || 0) > 0
                                    ? isDark
                                      ? '#38bdf8'
                                      : '#0284c7'
                                    : isDark
                                      ? '#94a3b8'
                                      : '#64748b',
                                fontSize: '0.75rem',
                                minWidth: '105px',
                                border: `1px solid ${(tag.iTotalFotos || 0) > 0
                                  ? isDark
                                    ? 'rgba(14, 165, 233, 0.35)'
                                    : '#bae6fd'
                                  : isDark
                                    ? 'rgba(255,255,255,0.05)'
                                    : '#e2e8f0'
                                  }`
                              }}
                            >
                              <FontAwesomeIcon icon={faCamera} style={{ fontSize: '0.70rem' }} />
                              <span>{tag.iTotalFotos === 1 ? '1 archivo' : `${tag.iTotalFotos || 0} archivos`}</span>
                            </span>
                          </div>
                        </td>

                        {/* ACCIONES (MENÚ DE 3 PUNTITOS) */}
                        <td className="pe-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                          <div className="d-flex align-items-center justify-content-center">
                            <Dropdown align="end" drop={isNearBottom ? 'up' : 'down'}>
                              <Dropdown.Toggle
                                as={TableRowMenuToggle}
                                isDark={isDark}
                                id={`tag-table-menu-${tag.idEtiqueta}`}
                              />

                              <Dropdown.Menu
                                className="shadow-lg border py-1"
                                style={{
                                  backgroundColor: isDark ? '#1a2636' : '#ffffff',
                                  borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
                                  borderRadius: '12px',
                                  minWidth: '150px',
                                  boxShadow: isDark ? '0 12px 30px rgba(0,0,0,0.5)' : '0 10px 25px rgba(0,0,0,0.08)',
                                  zIndex: 1060
                                }}
                              >
                                <Dropdown.Item
                                  as="button"
                                  className="d-flex align-items-center py-2 px-3"
                                  style={{
                                    gap: '10px',
                                    color: isDark ? '#f8fafc' : '#1e293b',
                                    fontSize: '0.83rem'
                                  }}
                                  onClick={() => handleOpenEdit(tag)}
                                >
                                  <FontAwesomeIcon
                                    icon={faEdit}
                                    style={{ width: '14px', fontSize: '0.82rem', color: isDark ? '#94a3b8' : '#64748b' }}
                                  />
                                  <span className="fw-medium">Editar</span>
                                </Dropdown.Item>

                                {!isResolved && (
                                  <Dropdown.Item
                                    as="button"
                                    className="d-flex align-items-center py-2 px-3"
                                    style={{
                                      gap: '10px',
                                      color: isDark ? '#f8fafc' : '#1e293b',
                                      fontSize: '0.83rem'
                                    }}
                                    onClick={() => handleOpenComplete(tag)}
                                  >
                                    <FontAwesomeIcon
                                      icon={faCheckCircle}
                                      style={{ width: '14px', fontSize: '0.82rem', color: '#10b981' }}
                                    />
                                    <span className="fw-medium">Resolver</span>
                                  </Dropdown.Item>
                                )}

                                <Dropdown.Divider style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0', margin: '4px 0' }} />

                                <Dropdown.Item
                                  as="button"
                                  className="d-flex align-items-center py-2 px-3 text-danger"
                                  style={{
                                    gap: '10px',
                                    color: '#ef4444',
                                    fontSize: '0.83rem'
                                  }}
                                  onClick={() => handleOpenDelete(tag)}
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    style={{ width: '14px', fontSize: '0.82rem', color: '#ef4444' }}
                                  />
                                  <span className="fw-medium">Eliminar</span>
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            /* VISTA EN CARDS */
            <div className="p-3.5">
              <div className="row g-3">
                {filteredTags.map(tag => (
                  <TagCard
                    key={tag.idEtiqueta}
                    tag={tag}
                    isDark={isDark}
                    onOpenView={handleOpenView}
                    onOpenEdit={handleOpenEdit}
                    onOpenComplete={handleOpenComplete}
                    onOpenDelete={handleOpenDelete}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Paginación */}
          <div className="p-3 pt-0">
            <TicketsPagination
              currentPage={page}
              totalPages={Math.ceil(totalRecords / pageSize) || 1}
              totalRecords={totalRecords}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              isLoading={isLoading}
              entityName="incidencias"
            />
          </div>
        </div>
      </div>

      {/* MODAL CREAR / EDITAR INCIDENCIA (IMAGEN 1) */}
      <TagCreateModal
        isOpen={isCreateModalOpen}
        selectedTag={selectedTag}
        formName={formName}
        setFormName={setFormName}
        formDescription={formDescription}
        setFormDescription={setFormDescription}
        formStatus={formStatus}
        setFormStatus={setFormStatus}
        formResponsible={formResponsible}
        setFormResponsible={setFormResponsible}
        agentsList={agentsList}
        isLoadingAgents={isLoadingAgents}
        filesList={filesList}
        setFilesList={setFilesList}
        isSaving={isSaving}
        onSave={handleSave}
        onClose={() => setIsCreateModalOpen(false)}
        isDark={isDark}
      />

      {/* MODAL VISTA DE INCIDENCIA ABIERTA (IMAGEN 2) */}
      <TagViewModal
        isOpen={isViewModalOpen}
        selectedTag={selectedTag}
        photosList={photosList}
        isLoadingPhotos={isLoadingPhotos}
        tagTickets={tagTickets}
        isLoadingTagTickets={isLoadingTagTickets}
        onClose={() => setIsViewModalOpen(false)}
        onOpenEdit={tag => {
          setIsViewModalOpen(false);
          handleOpenEdit(tag);
        }}
        onOpenComplete={handleOpenComplete}
        onOpenLightbox={setLightboxPhoto}
        isDark={isDark}
      />

      {/* MODAL ELIMINAR */}
      <TagDeleteModal
        itemToDelete={itemToDelete}
        isDeleting={isDeleting}
        isDark={isDark}
        onClose={() => setItemToDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      {/* MODAL COMPLETAR / RESOLVER INCIDENCIA */}
      <TagCompleteModal
        itemToComplete={itemToComplete}
        isCompleting={isCompleting}
        isDark={isDark}
        onClose={() => setItemToComplete(null)}
        onConfirm={handleConfirmComplete}
      />

      {/* MODAL LIGHTBOX FOTOS */}
      <TagPhotoModals
        lightboxPhoto={lightboxPhoto}
        onCloseLightbox={() => setLightboxPhoto(null)}
        photoToDelete={null}
        isDeletingPhoto={false}
        onCloseDeletePhoto={() => { }}
        onConfirmDeletePhoto={() => { }}
        isDark={isDark}
      />
    </div>
  );
};

export default TagsSettingsSection;
